import { escapeHTML } from '../../utils/escape.js';
import { MOTIVO_MAX_LEN } from '../../shared/timesheet-policy.js';

/**
 * Diálogo de anulación de una liquidación CONFIRMADA (Unit B2, design.md
 * AD12, spec `payroll-settlement-anulacion` "Anulación Is Visible and
 * Explained in the Admin Panel"). Función de render pura, mismo patrón que
 * `FichadaEditDialog`: `js/admin.js` (wiring) decide CUÁNDO mostrarlo y con
 * qué datos, este componente sólo dibuja.
 *
 * AD12 — por qué `FichadaEditDialog` y NO `ConfirmDialog`: `ConfirmDialog`
 * es solo-mensaje (sin campo) y sus hooks fijos (`reserva-confirm`/
 * `reserva-dismiss`) ya están cableados al flujo de CONFIRMAR una
 * liquidación en esta misma sección (`js/admin.js#renderLiquidacionConfirmMount`)
 * — agregarle un textarea cambiaría un componente compartido con Reservas.
 * `FichadaEditDialog` es el precedente exacto para esta forma: un `motivo`
 * obligatorio cuyo `required` es sólo UX, re-validado server-side.
 *
 * `motivo` (spec: "Confirmed row offers the action behind a mandatory
 * reason") SIEMPRE es obligatorio: el `<textarea required>` bloquea el
 * submit nativo antes de que cualquier listener corra — `anularLiquidacion`
 * (admin-liquidacion.service.js) y `anular_liquidacion` (migración 27)
 * vuelven a validarlo server-side, esto es solo UX. `maxlength` usa
 * `MOTIVO_MAX_LEN` (js/shared/timesheet-policy.js) — el mismo valor que el
 * servicio valida client-side y que `api/liquidacion.js#parsePayload` valida
 * server-side (review-5b84ba2ca338f75a follow-up).
 *
 * Este componente NO se renderiza dentro de `LiquidacionView` — vive en su
 * PROPIO mount, sibling del mount principal (`js/admin.js#mountLiquidacionSection`,
 * mismo patrón que el `ConfirmDialog` de confirmar liquidación), para que
 * abrir/cerrar el diálogo o mostrar un error de submit nunca repinte el
 * selector ni el historial de abajo.
 * @param {{ liquidacionId?: string, empleadoNombre?: string, periodo?: string, error?: string }|null} [pending]
 * @returns {string} HTML del diálogo, o `''` si no hay `liquidacionId` (diálogo cerrado).
 */
const FIELD =
    'w-full bg-base border border-border-subtle rounded-2xl px-4 py-3 text-sm text-text-primary focus:outline-none focus:border-brand';
const LABEL = 'block text-[10px] font-black uppercase tracking-[0.2em] text-text-muted mb-2';

export function AnularLiquidacionDialog(pending) {
    const { liquidacionId, empleadoNombre, periodo, error } = pending ?? {};
    if (!liquidacionId) return '';

    const nombre = escapeHTML(empleadoNombre ?? '');
    const periodoHtml = periodo ? ` — ${escapeHTML(periodo)}` : '';
    const errorHtml = error
        ? `<p class="text-xs text-red-500 font-bold mt-3" role="alert">${escapeHTML(error)}</p>`
        : '';

    return `
        <div role="dialog" aria-modal="true" aria-labelledby="liquidacion-anular-dialog-title" class="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 px-6">
            <div class="bg-base rounded-[2rem] p-8 max-w-sm w-full shadow-2xl">
                <p id="liquidacion-anular-dialog-title" class="text-sm font-bold text-text-primary text-center mb-2">Anular liquidación</p>
                <p class="text-xs text-text-muted text-center mb-6">${nombre}${periodoHtml}</p>
                <form data-id="${escapeHTML(liquidacionId)}">
                    <div class="mb-2">
                        <label class="${LABEL}">Motivo</label>
                        <textarea name="motivo" required maxlength="${MOTIVO_MAX_LEN}" rows="3" class="${FIELD}"></textarea>
                    </div>
                    ${errorHtml}
                    <div class="flex gap-3 mt-6">
                        <button type="button" data-action="liquidacion-anular-cancelar" class="flex-1 py-4 rounded-2xl border border-border-subtle text-xs font-black uppercase tracking-widest text-text-muted">Cancelar</button>
                        <button type="submit" data-action="liquidacion-anular-confirmar" class="flex-1 py-4 rounded-2xl bg-brand text-white text-xs font-black uppercase tracking-widest shadow-lg">Anular</button>
                    </div>
                </form>
            </div>
        </div>
    `;
}
